import { useState, useEffect } from "react";
import styles from "./Table.module.scss";
import { useGeoJsonData } from "../../GeoJsonDataContext";
import Filter from "../Filter/Filter";
import { buildFilterOptions } from "../../filterOptions";
import { isMobile as isMobileFromUserAgent } from "react-device-detect";

/**
 * Sortable/filterable table view of the same geoJsonData the Map renders,
 * with a condensed mobile layout (message + school only, date/postcode
 * folded into subtext). Takes no props; reads geoJsonData from context.
 */
const Table = () => {
  const { geoJsonData, loadError } = useGeoJsonData();
  const [filteredData, setFilteredData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    () => isMobileFromUserAgent || window.innerWidth <= 768
  );

  useEffect(() => {
    // Device type (from the user agent) doesn't change mid-session; only
    // track viewport width so a resize/orientation change still updates
    // the layout for devices react-device-detect doesn't flag as mobile.
    if (isMobileFromUserAgent) return;
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleInputChange = (selectedOption) => {
    if (geoJsonData) {
      if (!selectedOption || selectedOption.value === "all") {
        setFilteredData(geoJsonData.features);
      } else {
        setFilteredData(
          geoJsonData.features.filter(
            (feature) =>
              feature.properties.school
                .toLowerCase()
                .includes(selectedOption.value.toLowerCase()) ||
              (feature.properties.postcode &&
                feature.properties.postcode.includes(selectedOption.value))
          )
        );
      }
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (geoJsonData) {
      setFilteredData(
        [...geoJsonData.features].sort((a, b) => {
          const dateA = a.properties.date;
          const dateB = b.properties.date;
          return dateB - dateA;
        })
      );
      setTableLoading(false);
    }
  }, [geoJsonData]);

  // Formats a message's date relative to now: "today"/"yesterday" for the
  // first two days, "N days ago" through day 10, then a dd/mm/yy date.
  function daysAgo(date) {
    const now = new Date();
    const timeDifference = now - date; // Difference in milliseconds
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    if (daysDifference === 0) {
      return "today";
    } else if (daysDifference === 1) {
      return "yesterday";
    } else if (daysDifference > 10) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(2);
      return `${day}/${month}/${year}`;
    } else {
      return `${daysDifference} days ago`;
    }
  }

  // Without this the component renders nothing at all on failure, since the
  // guard below is just `geoJsonData && (...)` — a blank tab with no
  // explanation.
  if (loadError) {
    return (
      <div className={styles.tableContainer}>
        <p className={styles.errorMessage} role="alert">
          Sorry — the message data couldn’t be loaded. Please refresh to try
          again.
        </p>
      </div>
    );
  }

  return (
    geoJsonData && (
      <div className={styles.tableContainer}>
        <Filter
          options={buildFilterOptions(geoJsonData.features)}
          onInputChange={handleInputChange}
        />
        <table className={styles.Table} tabIndex="0">
          <caption>Messages for teachers from across New South Wales</caption>
          <thead>
            <tr>
              <th id="messageMessage" scope="col">
                Message
              </th>
              <th id="messageSchool" scope="col">
                School
              </th>
              {!isMobile && (
                <th id="messagePostcode" scope="col">
                  From postcode
                </th>
              )}
              {!isMobile && (
                <th id="messageDate" scope="col">
                  Date
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((entry, index) => (
              <tr key={index}>
                <td headers="messageMessage">
                  <span className={`${styles.message}`}>
                    {entry.properties.message}
                  </span>
                  {isMobile && (
                    <span className={`${styles.date}`}>
                      Date: {daysAgo(entry.properties.date)}
                    </span>
                  )}
                </td>
                <td headers="messageSchool">
                  <span className={`${styles.school}`}>
                    {entry.properties.school}
                  </span>
                  {isMobile && (
                    <span className={`${styles.postcode}`}>
                      Postcode: {entry.properties.postcode}
                    </span>
                  )}
                </td>
                {!isMobile && (
                  <td headers="messagePostcode">{entry.properties.postcode}</td>
                )}
                {!isMobile && (
                  <td headers="messageDate">
                    {daysAgo(entry.properties.date)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {tableLoading && (
          <div className={styles.overlay}>
            <div className={styles.spinner}></div>
          </div>
        )}
      </div>
    )
  );
};
export default Table;
